import React, { useCallback, useState, useContext, createContext } from 'react';
import type { ReactNode, FC } from 'react';
import { Box, Key, Text, useInput } from '@wix/cli-ui-kit';
import { Trans } from '@wix/cli-i18n';
import { useWixCliAppBi } from '../../bi/useWixCliAppBi.js';

interface WizardProps {
  steps: ReactNode[];
  name: string;
  onExitWizard: () => void;
}

interface WizardStepValues {
  isActive: boolean;
  goToNextStep: () => void;
  completeWizard: () => void;
}

const WizardStepContext = createContext<WizardStepValues | null>(null);

export function useWizardStep(): WizardStepValues {
  const context = useContext(WizardStepContext);
  if (context == null) {
    throw new Error(
      'useWizardStep() cannot be used without an <WizardStepContext.Provider>'
    );
  }
  return context;
}

const WizardStep: FC<{
  children: ReactNode;
  isActive: boolean;
  goToNextStep: () => void;
  completeWizard: () => void;
}> = ({ children, isActive, goToNextStep, completeWizard }) => (
  <WizardStepContext.Provider
    value={{ isActive, goToNextStep, completeWizard }}
  >
    {children}
  </WizardStepContext.Provider>
);

export const Wizard: FC<WizardProps> = ({ steps, name, onExitWizard }) => {
  const bi = useWixCliAppBi();
  const [activeStep, setActiveStep] = useState(0);
  const [wizardCompleted, setWizardCompleted] = useState(false);

  const stepCount = steps.length;

  const hasNextStep = activeStep < stepCount - 1;
  const hasPreviousStep = activeStep > 0;

  const goToNextStep = useCallback(() => {
    if (hasNextStep && !wizardCompleted) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, hasNextStep, wizardCompleted]);

  const goToPreviousStep = useCallback(() => {
    if (hasPreviousStep) {
      setActiveStep(activeStep - 1);
      bi.cliStepBack({
        additionalInfo: {
          activeStep,
          name,
        },
      });
    } else {
      onExitWizard();
    }
  }, [activeStep, hasPreviousStep, bi, name, onExitWizard]);

  useInput(
    (_, key) => {
      if (key.escape) {
        goToPreviousStep();
      }
    },
    { isActive: !wizardCompleted }
  );

  const completeWizard = useCallback(() => {
    setWizardCompleted(true);
  }, []);

  return (
    <>
      {steps.slice(0, activeStep + 1).map((step, idx) => (
        <WizardStep
          key={idx}
          isActive={idx === activeStep}
          goToNextStep={goToNextStep}
          completeWizard={completeWizard}
        >
          {step}
        </WizardStep>
      ))}
      {!wizardCompleted && (
        <Box marginTop={1}>
          <Text skin="secondary">
            <Trans
              i18nKey="generate_command.go_back"
              components={[<Key value="Esc" skin="secondary" />]}
            />
          </Text>
        </Box>
      )}
    </>
  );
};
